import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const InstitutionCard = ({ institution }) => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();

  const handleViewDetails = () => {
    navigate('/institution-profile', { state: { institution } });
  };

  const handleStartApplication = () => {
    if (!isSignedIn) {
      navigate('/user-login');
    } else {
      navigate('/application-form', { state: { institution } });
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-warm hover:shadow-warm-md transition-smooth border border-border overflow-hidden group">
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden bg-muted">
        <Image
          src={institution?.image}
          alt={institution?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
        />
        {institution?.featured && (
          <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium font-caption flex items-center gap-1">
            <Icon name="Star" size={14} />
            Featured
          </div>
        )}
        {institution?.accredited && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium font-caption flex items-center gap-1">
            <Icon name="BadgeCheck" size={14} />
            Accredited
          </div>
        )}
      </div>
      <div className="p-4 md:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0 bg-background rounded-lg overflow-hidden border border-border">
            <Image
              src={institution?.logo}
              alt={institution?.logoAlt}
              className="w-full h-full object-contain p-2"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1 line-clamp-2">
              {institution?.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={16} />
              <span className="font-caption">{institution?.location}</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="BookOpen" size={16} color="var(--color-primary)" />
            <span className="font-caption">{institution?.programType}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} color="var(--color-primary)" />
            <span className="font-caption">{institution?.duration}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Users" size={16} color="var(--color-primary)" />
            <span className="font-caption">{institution?.studentCount} international students</span>
          </div>
        </div>

        <div className="bg-accent/10 rounded-md p-3 mb-4 border border-accent/20">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground font-caption">
              Application Deadline
            </span>
            <span className="text-sm font-semibold text-accent data-text">
              {institution?.deadline}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {institution?.highlights?.map((highlight, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-caption"
            >
              {highlight}
            </span>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            fullWidth
            iconName="Eye"
            iconPosition="left"
            onClick={handleViewDetails}
          >
            View Details
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="PenTool"
            iconPosition="left"
            onClick={handleStartApplication}
          >
            Apply Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstitutionCard;